import { Box, TextField } from "@mui/material";
import {
  FieldInputProps,
  FieldMetaProps,
  FastField,
  Field,
  FormikBag,
} from "formik";
import { ReactElement, useMemo, useRef } from "react";
import * as Yup from "yup";

import {
  EncodingStats,
  SMSTextEncodingTenant,
  getEncodingStats,
} from "@stw/sms-text-encoding";

import { useTenant } from "~frontend/providers/LoggedInUserProvider";
import {
  SegmentEstimate,
  SegmentEstimateDetail,
} from "~frontend/routes/subaccounts/campaignEditor/Script/SegmentEstimate";

import { FlexBox } from "../FlexBox";
import { ScriptFieldLabel } from "./FormScriptField";

type FormPlainScriptFieldProps = {
  label?: string;
  value: string;
  error?: string;
  strictLengthLimit?: boolean;
  onChange?: (value: string, error: string | null) => void;
  onBlur?: () => void;
  hasAttachments?: boolean;
};

function validateScriptField(
  stats: EncodingStats,
  { strictLengthLimit }: { strictLengthLimit: boolean }
): string | null {
  if (strictLengthLimit && stats.charCount > stats.maxCharsForWarning) {
    return `Message exceeds maximum of ${stats.maxCharsForWarning} characters`;
  } else if (stats.charCount > stats.maxChars) {
    return `Message exceeds maximum of ${stats.maxChars} characters`;
  }

  return null;
}

export default function FormPlainScriptField({
  value,
  onChange,
  onBlur,
  label,
  error,
  strictLengthLimit = false,
  hasAttachments = false,
}: FormPlainScriptFieldProps): ReactElement {
  const tenant = useTenant()!;

  const encodingStatsRef = useRef<EncodingStats>(
    getEncodingStats(
      {
        text: value,
        hasAttachments,
      },
      tenant
    )
  );

  const encodingStats = useMemo(() => {
    return getEncodingStats(
      {
        text: value,
        hasAttachments,
      },
      tenant
    );
  }, [value, tenant, hasAttachments]);

  return (
    <Box sx={{ mt: 2 }}>
      <FlexBox justifyContent="end" sx={{ mb: 1 }}>
        <SegmentEstimate encodingStats={encodingStats} />
      </FlexBox>
      <ScriptFieldLabel
        label={label}
        secondaryLabel={
          <SegmentEstimateDetail
            encodingStats={encodingStats}
            hasAttachments={hasAttachments}
          />
        }
      />
      <TextField
        sx={{ mt: 0, pt: 0 }}
        multiline
        minRows={4}
        fullWidth
        value={value}
        onChange={evt => {
          const stats = getEncodingStats(
            {
              text: evt.target.value,
              hasAttachments,
            },
            tenant
          );

          encodingStatsRef.current = stats;
          onChange?.(
            evt.target.value,
            validateScriptField(stats, { strictLengthLimit })
          );
        }}
        error={Boolean(error)}
        helperText={error}
        onBlur={onBlur}
      />
    </Box>
  );
}

export function FormikPlainScriptField({
  name,
  shouldUpdate,
  fast,
  label,
  strictLengthLimit = false,
  hasAttachments = false,
}: {
  name: string;
  shouldUpdate?: (props: any, prevProps: any) => boolean;
  fast?: boolean;
  label?: string;
  strictLengthLimit?: boolean;
  hasAttachments?: boolean;
}): ReactElement {
  const Component = fast ? FastField : Field;
  return (
    <Component name={name} shouldUpdate={shouldUpdate}>
      {({
        field,
        meta: meta,
        form,
      }: {
        field: FieldInputProps<unknown>;
        meta: FieldMetaProps<unknown>;
        form: FormikBag<unknown, unknown>;
      }) => {
        function handleChange(newVal: string, error: string | null) {
          form.setFieldValue(name, newVal, false).catch(console.error);
          form.setFieldError(name, error || undefined);
        }

        return (
          <FormPlainScriptField
            value={field.value as string}
            onChange={handleChange}
            label={label}
            error={
              meta.error && meta.touched ? (meta.error as string) : undefined
            }
            onBlur={() => form.setFieldTouched(name, true, true)}
            strictLengthLimit={strictLengthLimit}
            hasAttachments={hasAttachments}
          />
        );
      }}
    </Component>
  );
}

export function plainScriptFieldValidator(
  {
    required = false,
    strictLengthLimit = false,
    hasAttachments = false,
  }: {
    required?: boolean;
    strictLengthLimit?: boolean;
    hasAttachments?: boolean;
  },
  tenant: SMSTextEncodingTenant
): Yup.StringSchema {
  let schema = Yup.string();

  if (required) {
    schema = schema.required("Message is required");
  }

  return schema.test(
    "length",
    "Message exceeds maximum length",
    function (value, context) {
      if (!value) {
        return true;
      }
      const stats = getEncodingStats(
        {
          text: value,
          hasAttachments,
        },
        tenant
      );

      const err = validateScriptField(stats, { strictLengthLimit });
      if (!err) {
        return true;
      }

      return this.createError({ message: err, path: context.path });
    }
  );
}
